<template>
  <div class="space-y-5">
    <!-- Section header -->
    <div class="flex items-center gap-3">
      <span
        class="flex items-center justify-center w-9 h-9 rounded-lg bg-taxiye-100 text-taxiye-500"
      >
        <span class="material-icons-round">admin_panel_settings</span>
      </span>
      <h3 class="text-base font-semibold text-gray-800">RBAC</h3>
    </div>

    <!-- Roles + create -->
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          v-for="role in roles"
          :key="role.roleName"
          @click="selectRole(role)"
          class="px-4 h-10 flex items-center text-sm font-medium border rounded-xl transition-colors duration-150 focus:outline-none"
          :class="
            activeRole.roleName === role.roleName
              ? 'bg-taxiye-500 border-taxiye-500 text-white shadow-sm'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          "
        >
          {{ role.roleName }}
        </button>
      </div>

      <div class="flex items-end">
        <div
          v-if="creatingNewRole"
          class="flex flex-wrap items-end gap-3 p-3 border border-gray-200 bg-gray-50 rounded-xl"
        >
          <div class="flex flex-col">
            <span class="mb-1 text-xs text-gray-500">New Role</span>
            <input
              type="text"
              v-model="newRole"
              class="h-10 text-sm border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
            />
          </div>
          <button
            type="button"
            class="inline-flex items-center h-10 px-4 text-sm font-semibold text-white transition-colors duration-150 shadow-sm cursor-pointer rounded-xl bg-taxiye-500 hover:bg-taxiye-600"
            @click="createRole()"
          >
            <span
              v-if="creatingRBAC"
              class="mr-1 text-base material-icons-round animate-spin"
              >loop</span
            >
            <span v-else class="mr-1 text-base material-icons-round"
              >check</span
            >
            Done
          </button>
          <button
            type="button"
            class="inline-flex items-center h-10 px-4 text-sm font-medium text-gray-700 transition-colors duration-150 bg-white border border-gray-300 cursor-pointer rounded-xl hover:bg-gray-50"
            @click="creatingNewRole = false"
          >
            <span class="mr-1 text-base material-icons-round">cancel</span>
            Cancel
          </button>
        </div>
        <button
          v-else
          type="button"
          @click="creatingNewRole = true"
          class="inline-flex items-center h-10 px-4 text-sm font-semibold text-white transition-colors duration-150 shadow-sm cursor-pointer rounded-xl bg-taxiye-500 hover:bg-taxiye-600"
        >
          <span class="mr-1 text-base material-icons-round">add</span>
          Create a new Role
        </button>
      </div>
    </div>

    <transition name="fade">
      <div
        class="flex justify-end w-full"
        v-if="passengerId && selectedPassenger"
      >
        <div class="inline-flex gap-3">
          <div class="px-4 py-2 border border-gray-200 rounded-xl bg-gray-50">
            <p class="text-xs text-gray-500">Selected Passenger</p>
            <p class="text-2xl font-semibold text-gray-800">
              {{ passengerName }}
            </p>
          </div>
          <div class="px-4 py-2 border border-gray-200 rounded-xl bg-gray-50">
            <p class="text-xs text-gray-500">Balance</p>
            <p class="text-2xl font-semibold text-gray-800">
              {{ passengerBalance }}
            </p>
          </div>
        </div>
      </div>
    </transition>

    <!-- Permissions card -->
    <div
      class="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-2xl"
    >
      <div
        class="flex flex-wrap items-center justify-between gap-4 p-4 border-b border-gray-200"
      >
        <span class="text-sm font-medium text-gray-700">
          Choose modules are accessible from
          {{ activeRole.roleName }} role
        </span>
        <div class="flex gap-3">
          <button
            type="button"
            class="inline-flex items-center h-10 px-4 text-sm font-semibold text-white transition-colors duration-150 shadow-sm cursor-pointer rounded-xl bg-taxiye-500 hover:bg-taxiye-600"
            @click="saveRBAC()"
            :disabled="activeRole.roleName === 'Super Admin'"
          >
            <span
              v-if="savingRBAC"
              class="mr-1 text-base material-icons-round animate-spin"
              >loop</span
            >
            <span v-else class="mr-1 text-base material-icons-round">save</span>
            Save
          </button>
          <button
            type="button"
            class="inline-flex items-center h-10 px-4 text-sm font-medium text-red-600 transition-colors duration-150 bg-white border border-red-200 cursor-pointer rounded-xl hover:bg-red-50"
            @click="deleteRBAC()"
            :disabled="activeRole.roleName === 'Super Admin'"
          >
            <span
              v-if="deletingRBAC"
              class="mr-1 text-base material-icons-round animate-spin"
              >loop</span
            >
            <span v-else class="mr-1 text-base material-icons-round"
              >delete</span
            >
            Delete Role
          </button>
        </div>
      </div>
      <role-permissions
        :role-name="activeRole.roleName"
        v-model="activeRole.permissions"
      ></role-permissions>
    </div>
  </div>
</template>

<script>
import RolePermissions from "@/components/RolePermissions";

// import moment from 'moment';

export default {
  props: ["corporate"],
  data() {
    return {
      allTickets: [],
      employeeName: "",
      openDropdown: false,
      loading: true,
      searchKey: "",
      confirmOpen: false,
      deleting: false,
      results: [],
      selectedActiveFilter: "both",

      roles: [],

      activeRole: {},

      creatingNewRole: false,
      newRole: "",

      savingRBAC: false,
      deletingRBAC: false,
      creatingRBAC: false,
    };
  },
  components: {
    RolePermissions,
  },
  mounted() {
    document.body.addEventListener("keyup", this.escapeListener);
  },
  beforeDestroy() {
    document.body.removeEventListener("keyup", this.escapeListener);
  },
  watch: {
    corporate() {
      this.getTickets();
    },
    selectedActiveFilter() {
      this.getTickets();
    },
    currentPage() {
      this.getTickets();
    },
    perPage() {
      this.currentPage === 1 ? this.getTickets() : (this.currentPage = 1);
    },
  },
  created() {
    this.fetchRoles();
  },
  computed: {
    tickets() {
      return this.results.length ? this.results : this.allTickets;
    },
  },
  methods: {
    setActiveFilter(e) {
      this.selectedActiveFilter = e.target.value;
    },
    escapeListener(e) {
      if (e.keyCode === 27) {
        this.closeConfirmBox();
      }
    },
    formatDate(date) {
      // return moment(date).format("MMM D, YYYY HH:mm");
      return date ? date.replace("T", " ").slice(0, 16) : "-";
    },

    fetchRoles() {
      this.$http
        .get(`/corporates/${this.corporate._id}/roles/`)
        .then(({ data: { data } }) => {
          this.roles = data;
          if (this.roles && this.roles.length) this.activeRole = this.roles[0];
        });
    },
    selectRole(role) {
      this.activeRole = role;
    },
    saveRBAC() {
      this.savingRBAC = true;
      this.$http
        .patch(
          `/corporates/${this.corporate._id}/roles/${this.activeRole._id}`,
          this.activeRole
        )
        .then(() => {
          window.flash({
            type: "success",
            body: "Role updated successfully!",
          });
        })
        .catch(err => {
          if (
            err.response &&
            err.response.data &&
            err.response.data.message &&
            err.response.data.message.includes("ValidationError")
          ) {
            window.flash({
              type: "error",
              body: err.response.data.message
                .split(":")
                .slice(1)
                .join(":")
                .split(",")
                .join("<br/>"),
            });
            console.log(err);
          } else {
            window.flash({
              type: "error",
              body: "Role couldn't be updated!",
            });
            console.log(err);
          }
        })
        .finally(() => (this.savingRBAC = false));
    },
    deleteRBAC() {
      this.deletingRBAC = true;
      this.$http
        .delete(
          `/corporates/${this.corporate._id}/roles/${this.activeRole._id}`
        )
        .then(() => {
          window.flash({
            type: "success",
            body: "Role deleted successfully!",
          });
          this.fetchRoles();
        })
        .catch(err => {
          if (
            err.response &&
            err.response.data &&
            err.response.data.message &&
            err.response.data.message.includes("ValidationError")
          ) {
            window.flash({
              type: "error",
              body: err.response.data.message
                .split(":")
                .slice(1)
                .join(":")
                .split(",")
                .join("<br/>"),
            });
            console.log(err);
          } else {
            window.flash({
              type: "error",
              body: "Error: couldn't delete the role.",
            });
            console.log(err);
          }
        })
        .finally(() => (this.deletingRBAC = false));
    },
    createRole() {
      this.creatingRBAC = true;
      this.$http
        .post(`/corporates/${this.corporate._id}/roles/`, {
          roleName: this.newRole,
        })
        .then(() => {
          this.creatingNewRole = false;
          this.fetchRoles();
        })
        .catch(err => console.log(err))
        .finally(() => (this.creatingRBAC = false));
    },
  },
};
</script>
